
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeft, ImageIcon, X, Check, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import ChatBot from "@/components/ChatBot";
import { useVisionBoard } from "@/hooks/useVisionBoard";
import { cn } from "@/lib/utils";
import { loadSelectedChatBackground, saveSelectedChatBackground } from "@/utils/chatBackdropStorage";

const DashboardChat = () => {
  const { images } = useVisionBoard();
  const [bgUrl, setBgUrl] = useState<string | null>(null);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [pickerPos, setPickerPos] = useState({ top: 0, right: 0 });

  // Restore the background for the current account, including legacy selections.
  useEffect(() => {
    let active = true;
    loadSelectedChatBackground().then((savedUrl) => {
      if (!active) return;
      setBgUrl(savedUrl);
      setBackgroundLoaded(true);
    });
    return () => { active = false; };
  }, []);

  // Persist changes only after restoration so startup cannot erase the saved value.
  useEffect(() => {
    if (!backgroundLoaded) return;
    void saveSelectedChatBackground(bgUrl);
  }, [bgUrl, backgroundLoaded]);

  // Position the portal picker relative to the trigger button
  const openPicker = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPickerPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setPickerOpen((o) => !o);
  };

  // Close picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        pickerRef.current && !pickerRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setPickerOpen(false);
      }
    };
    if (pickerOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const selectBg = (url: string) => {
    setBgUrl(url === bgUrl ? null : url);
    setPickerOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative w-full max-w-full">
      {/* Background layer — video or image */}
      {bgUrl && (
        bgUrl.match(/\.(mp4|webm|mov)(\?|$)/i) ? (
          <video
            key={bgUrl}
            src={bgUrl}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 z-0 w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bgUrl})` }}
          />
        )
      )}

      {/* Subtle overlay for text readability */}
      {bgUrl && (
        <div className="absolute inset-0 z-0 bg-background/20" />
      )}

      {/* Header */}
      <div className="relative z-10 border-b border-border px-4 py-3 flex items-center gap-3 bg-background/60 backdrop-blur-md">
        <Link to="/dashboard">
          <Button variant="ghost" size="icon" className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-lg font-semibold text-foreground flex-1">DM the Universe</h1>

        {/* Clear background button */}
        {bgUrl && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setBgUrl(null)}
            className="shrink-0 text-muted-foreground hover:text-destructive"
            title="Remove background"
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        {/* Picker trigger */}
        <Button
          ref={triggerRef}
          variant="ghost"
          size="icon"
          onClick={openPicker}
          className={cn("shrink-0 relative", bgUrl && "text-primary")}
          title="Set background from Vision Board"
        >
          <ImageIcon className="h-5 w-5" />
          {bgUrl && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
          )}
        </Button>
      </div>

      {/* Chat content */}
      <div className="relative z-10 flex-1 overflow-hidden">
        <ChatBot />
      </div>

      {/* Portal picker — rendered on document.body, above everything */}
      {pickerOpen && createPortal(
        <div
          ref={pickerRef}
          style={{ position: "fixed", top: pickerPos.top, right: pickerPos.right, zIndex: 99999 }}
          className="w-72 bg-popover border border-border rounded-xl shadow-2xl p-3"
        >
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Pick a background from your Vision Board
          </p>
          {images.length === 0 ? (
            <p className="text-xs text-muted-foreground italic text-center py-4">
              No images on your Vision Board yet. Add some first!
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {images.map((img) => (
                <button
                  key={img.id}
                  onClick={() => selectBg(img.url)}
                  className={cn(
                    "relative aspect-video rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 focus:outline-none",
                    bgUrl === img.url ? "border-primary" : "border-transparent"
                  )}
                >
                  {img.type === "video" ? (
                    <>
                      <video
                        src={img.url}
                        muted
                        loop
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-1 right-1 bg-black/60 rounded px-1">
                        <Film className="h-2.5 w-2.5 text-white" />
                      </div>
                    </>
                  ) : (
                    <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                  )}
                  {bgUrl === img.url && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary-foreground drop-shadow" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DashboardChat;
